// src/components/engine/TimelineView.jsx
// §4.3 Timeline UI — главный интерфейс доверия
//
// Показывает решения engine для кампании:
//   время, risk_status, action, mode, confidence (low/medium/high), reasons[]

import { useState, useEffect } from "react";
import { workerClient }        from "../../api/workerClient";

const ACTION_COLORS = {
  none:    "var(--text-muted)",
  observe: "var(--text-muted)",
  alert:   "#f5a623",
  reduce:  "#f5a623",
  pause:   "#e85555",
};

const RISK_COLORS = {
  low:      "#12cc76",
  medium:   "#f5a623",
  high:     "#e85555",
  critical: "#ff3333",
};

const CONF_LABELS = { high: "HIGH", medium: "MED", low: "LOW" };

function fmtTs(ts) {
  const d = new Date(ts * 1000);
  return d.toLocaleString("en-GB", {
    month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function confLevel(c) {
  if (c >= 0.75) return "high";
  if (c >= 0.5)  return "medium";
  return "low";
}

/**
 * @param {{ campaignId: string, limit?: number }} props
 */
export default function TimelineView({ campaignId, limit = 30 }) {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!campaignId) return;
    const path = `/api/engine/timeline?campaign_id=${campaignId}&limit=${limit}`;
    workerClient(path)
      .then(res => {
        if (res.ok) setEvents(res.events ?? []);
        else        setError("Failed to load timeline");
      })
      .catch(() => setError("Network error"))
      .finally(() => setLoading(false));
  }, [campaignId, limit]);

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {[1, 2, 3].map(i => (
          <div key={i} className="skeleton" style={{ height: 80, borderRadius: "var(--radius)" }} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--danger)" }}>
        {error}
      </div>
    );
  }

  if (!events.length) {
    return (
      <div style={{
        padding: "20px", borderRadius: "var(--radius)",
        background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
        fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)",
        textAlign: "center",
      }}>
        No decisions recorded yet. Run engine to start.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {events.map((e, idx) => {
        const actionColor = ACTION_COLORS[e.action] ?? "var(--text-secondary)";
        const riskColor   = RISK_COLORS[e.risk_status] ?? "var(--text-muted)";
        const confLvl     = confLevel(e.confidence);
        const isFirst     = idx === 0;

        return (
          <div key={e.id} style={{
            padding:      "14px 18px",
            borderRadius: "var(--radius)",
            background:   isFirst ? "var(--bg-elevated)" : "var(--bg-card)",
            border:       `1px solid ${isFirst ? "var(--border)" : "var(--border-subtle)"}`,
            marginBottom: isFirst ? 2 : 0,
          }}>
            {/* Row 1: timestamp + action + risk */}
            <div style={{
              display: "flex", alignItems: "center",
              gap: 12, marginBottom: 8,
            }}>
              <span style={{
                fontFamily: "var(--font-mono)", fontSize: 10,
                color: "var(--text-muted)", minWidth: 110,
              }}>
                {fmtTs(e.timestamp)}
              </span>

              {/* Action */}
              <span style={{
                fontFamily:    "var(--font-mono)", fontSize: 10,
                fontWeight:    700, color: actionColor,
                letterSpacing: "0.1em", textTransform: "uppercase",
                padding:       "2px 8px",
                background:    `${actionColor}15`,
                borderRadius:  "var(--radius-sm)",
                border:        `1px solid ${actionColor}30`,
              }}>
                {e.action}
              </span>

              {/* Risk status */}
              <span style={{
                fontFamily:    "var(--font-mono)", fontSize: 10,
                color:         riskColor,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}>
                {e.risk_status}
              </span>

              <div style={{ flex: 1 }} />

              {/* Confidence */}
              <span style={{
                fontFamily:    "var(--font-mono)", fontSize: 9,
                color:         "var(--text-muted)",
                textTransform: "uppercase",
              }}>
                CONF {CONF_LABELS[confLvl]}
              </span>

              {/* Mode */}
              <span style={{
                fontFamily:    "var(--font-mono)", fontSize: 9,
                color:         "var(--text-tertiary)",
                textTransform: "uppercase",
              }}>
                {e.mode}
              </span>
            </div>

            {/* Row 2: reasons */}
            {e.reasons?.length > 0 && (
              <div style={{
                display:       "flex",
                flexDirection: "column",
                gap:           3,
              }}>
                {e.reasons.map((r, i) => (
                  <div key={i} style={{
                    fontFamily: "var(--font-mono)", fontSize: 11,
                    color:      "var(--text-secondary)",
                    paddingLeft: 12,
                    borderLeft: `2px solid ${actionColor}40`,
                  }}>
                    {r}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
